import {
  PanoramaControls,
  Quaternion,
  Vector3,
  Web3DViewer,
} from '@technology/web3d';
import {
  PanoramaMetadata,
  PanoramaModel,
  PanoramaPlugin,
} from '@technology/web3d-plugin-panorama';
import {
  buildTilesUrlFromTemplateUrl,
  ImageStation,
  PanoramaImageStation,
  StationsCapabilityProvider,
  Web3dTool,
} from '@trimble/web3d-providers';
import { Accessor } from 'solid-js';
import { Web3dSettings } from '../models/web3d-settings';
import {
  ImageCapabilityBase,
  ImageCapabilityOpacityOptions,
} from './image-capability-base';
import { ThreeJsManager } from '@trimble/web3d-utils';
import {
  PANORAMA_DEBUG,
  PANORAMA_DEFAULT_TILE_HEIGHT,
  PANORAMA_FOV,
  PANORAMA_IS_LOWER_LEFT_ORIGIN,
  PANORAMA_IS_URL_LEVEL_ZERO_BASED,
  PANORAMA_LOD_ADJUSTMENT,
  PANORAMA_MIN_LOD_LEVEL,
  PANORAMA_RENDER_ORDER,
  PANORAMA_SPHERE_RADIUS,
  PANORAMA_STABILIZATION_DELAY_MS,
} from './constants/panorama-constants';
import { NavigationTool } from '../helpers/web3d-tool-helper';

export class PanoramaCapability extends ImageCapabilityBase {
  protected override requiredWeb3DTools: NavigationTool[] = [
    Web3dTool.PanoramaControls,
  ];
  private lastStationQuaternion: Quaternion | null = null;

  constructor(
    stationsProvider: StationsCapabilityProvider,
    viewer: Web3DViewer,
    private readonly panoramaPlugin: PanoramaPlugin,
    cameraName: string,
    web3dSettings: Accessor<Web3dSettings>,
    threeJsManager: ThreeJsManager,
    opacityOptions?: ImageCapabilityOpacityOptions
  ) {
    super(
      stationsProvider,
      cameraName,
      viewer,
      web3dSettings,
      threeJsManager,
      opacityOptions
    );

    // Render panorama behind the point cloud
    panoramaPlugin.renderOrder = PANORAMA_RENDER_ORDER;
  }

  public async loadImageStation(
    imageStation: PanoramaImageStation
  ): Promise<PanoramaModel | undefined> {
    if (
      !this.isActive() ||
      imageStation.station.tilesetInfo?.tilesetUrl === undefined
    ) {
      return Promise.resolve(undefined);
    }

    const tilesUrl = buildTilesUrlFromTemplateUrl(
      imageStation.station.tilesetInfo.tilesetUrl
    );

    const panoramaControls = this.viewer.tools.get<
      PanoramaControls | undefined
    >(PanoramaControls);

    if (panoramaControls == undefined) {
      throw new Error("Panorama controls couldn't be retrieved!");
    }

    const metadata: PanoramaMetadata = {
      id: imageStation.station.id,
      quaternion: new Quaternion(
        imageStation.station.quaternion.x ?? 0,
        imageStation.station.quaternion.y ?? 0,
        imageStation.station.quaternion.z ?? 0,
        imageStation.station.quaternion.w ?? 0
      ),
      tilesUrl: tilesUrl,
      tileHeight:
        imageStation.station.tilesetInfo.tileHeight != undefined &&
        imageStation.station.tilesetInfo.tileHeight > 0
          ? imageStation.station.tilesetInfo.tileHeight
          : PANORAMA_DEFAULT_TILE_HEIGHT,
      isLowerLeftOrigin: PANORAMA_IS_LOWER_LEFT_ORIGIN,
      maxWidth: imageStation.station.tilesetInfo.resizedWidth ?? 0,
      maxHeight: imageStation.station.tilesetInfo.resizedHeight ?? 0,
      sphereRadius: PANORAMA_SPHERE_RADIUS,
      position: new Vector3(
        imageStation.station.position.x,
        imageStation.station.position.y,
        imageStation.station.position.z
      ),
      fovTop: PANORAMA_FOV.top,
      fovBottom: PANORAMA_FOV.bottom,
      fovLeft: PANORAMA_FOV.left,
      fovRight: PANORAMA_FOV.right,
      minLodLevel: PANORAMA_MIN_LOD_LEVEL,
      lodAdjustment: PANORAMA_LOD_ADJUSTMENT,
      minFov: panoramaControls.minFov,
      maxFov: panoramaControls.maxFov,
      isUrlLevelZeroBased:
        imageStation.station.tilesetInfo?.isUrlLevelZeroBased ??
        PANORAMA_IS_URL_LEVEL_ZERO_BASED,
      stabilizationDelayMs: PANORAMA_STABILIZATION_DELAY_MS,
      debug: PANORAMA_DEBUG,
    };

    const panoramaModel = this.viewer.getModel(metadata.id);
    if (panoramaModel) return panoramaModel as PanoramaModel;

    const model = await this.panoramaPlugin.load(metadata);
    return model;
  }

  protected setupCameraForStation(imageStation: ImageStation): void {
    this.viewer.camera.position = new Vector3(
      imageStation.station.position.x,
      imageStation.station.position.y,
      imageStation.station.position.z
    );

    const stationQuaternion = toStationQuaternion(imageStation);

    if (this.lastStationQuaternion) {
      const stationInverse = this.lastStationQuaternion.clone().invert();
      const localOffset = stationInverse
        .multiply(this.viewer.camera.quaternion.clone())
        .normalize();
      this.viewer.camera.quaternion = stationQuaternion
        .clone()
        .multiply(localOffset)
        .normalize();
    }

    this.lastStationQuaternion = stationQuaternion;
  }

  public override applyCameraOrientation(
    quaternion: Quaternion,
    fov?: number
  ): void {
    super.applyCameraOrientation(quaternion, fov);
    // The applied orientation is already in world space for the selected
    // station. Re-basing it when that station's image finishes loading would
    // rotate the camera outside the sync, and the sync loop would echo that
    // rotation back into the pane that drove the sync.
    const selectedIndex = this.selectedStationIndex();
    const selectedStation =
      selectedIndex == null ? null : this.stationList()?.[selectedIndex];
    this.lastStationQuaternion = selectedStation
      ? toStationQuaternion(selectedStation)
      : null;
  }

  public override async cleanup(): Promise<void> {
    this.lastStationQuaternion = null;
    await super.cleanup();
  }
}

function toStationQuaternion(imageStation: ImageStation): Quaternion {
  return new Quaternion(
    imageStation.station.quaternion?.x ?? 0,
    imageStation.station.quaternion?.y ?? 0,
    imageStation.station.quaternion?.z ?? 0,
    imageStation.station.quaternion?.w ?? 1
  ).normalize();
}
